import { Controller } from "@hotwired/stimulus"
import { ArcadeStage } from "../utils/arcade_stage"

// 2048, sans variante : une grille 4 × 4, on pousse, les tuiles égales fusionnent, et le
// score est la somme des fusions.
//
// Les tuiles GLISSENT. C'est tout l'intérêt du jeu : sans l'animation, on ne voit pas d'où
// vient une fusion et la grille semble se réécrire au hasard. Chaque tuile est donc un nœud
// qui vit d'un coup à l'autre, positionné en `transform` — la grille de fond, elle, est
// posée une fois pour toutes.
//
// Un coup se joue en deux temps : on déplace tout le monde (transition CSS), puis, la
// transition finie, on retire les tuiles absorbées et on met à jour la valeur de celles qui
// ont fusionné. Sans ce délai, la tuile absorbée disparaîtrait avant d'avoir glissé.
const SIZE = 4
const WIN = 2048
const SLIDE_MS = 110

export default class extends Controller {
  static values = { labels: Object, scoresUrl: String }

  start() {
    this.stage = new ArcadeStage({
      labels: this.labelsValue,
      scoresUrl: this.scoresUrlValue,
      onRestart: () => this.#play(),
      onClose: () => this.#stop(),
      // 2048 ne se perd pas forcément : tant qu'il reste un coup, la partie continue. Sans
      // ce bouton, la seule sortie d'une partie qu'on veut ranger était la croix — qui ferme
      // la borne sans enregistrer le score.
      onStop: () => this.#finish(this.labelsValue.stopped),
    })
    this.stage.open()

    this.board = document.createElement("div")
    this.board.className = "arcade-2048"
    this.board.append(this.#backdrop(), this.#layer())
    this.stage.board.appendChild(this.board)

    this.onKey = (event) => this.#key(event)
    document.addEventListener("keydown", this.onKey)
    this.onTouchStart = (event) => {
      const touch = event.changedTouches[0]
      this.touchOrigin = { x: touch.clientX, y: touch.clientY }
    }
    this.onTouchEnd = (event) => this.#swipe(event)
    this.board.addEventListener("touchstart", this.onTouchStart, { passive: true })
    this.board.addEventListener("touchend", this.onTouchEnd, { passive: true })

    this.#play()
  }

  #stop() {
    window.clearTimeout(this.timer)
    document.removeEventListener("keydown", this.onKey)
    this.board?.removeEventListener("touchstart", this.onTouchStart)
    this.board?.removeEventListener("touchend", this.onTouchEnd)
    this.board = null
  }

  // Les seize cases vides, peintes une fois : elles ne bougent jamais, et elles donnent à la
  // grille sa géométrie même quand il n'y a qu'une tuile dessus.
  #backdrop() {
    const backdrop = document.createElement("div")
    backdrop.className = "arcade-2048__backdrop"
    for (let index = 0; index < SIZE * SIZE; index += 1) {
      const cell = document.createElement("div")
      cell.className = "arcade-2048__cell"
      backdrop.appendChild(cell)
    }
    return backdrop
  }

  #layer() {
    this.tileLayer = document.createElement("div")
    this.tileLayer.className = "arcade-2048__tiles"
    return this.tileLayer
  }

  #play() {
    window.clearTimeout(this.timer)
    this.tiles = []
    this.nextId = 1
    this.over = false
    this.busy = false
    this.tileLayer.replaceChildren()
    this.stage.setScore(0)
    this.stage.setHint(this.labelsValue.hint || "")
    this.#spawn()
    this.#spawn()
  }

  #key(event) {
    const moves = {
      ArrowLeft: "left", ArrowRight: "right", ArrowUp: "up", ArrowDown: "down",
      q: "left", a: "left", d: "right", z: "up", w: "up", s: "down",
    }
    const move = moves[event.key] || moves[event.key?.toLowerCase?.()]
    if (!move) return

    event.preventDefault()
    this.#move(move)
  }

  #swipe(event) {
    if (!this.touchOrigin) return

    const touch = event.changedTouches[0]
    const dx = touch.clientX - this.touchOrigin.x
    const dy = touch.clientY - this.touchOrigin.y
    this.touchOrigin = null
    if (Math.hypot(dx, dy) < 24) return

    if (Math.abs(dx) > Math.abs(dy)) this.#move(dx > 0 ? "right" : "left")
    else this.#move(dy > 0 ? "down" : "up")
  }

  // Une seule mécanique de poussée, appliquée à des LIGNES : les quatre directions ne sont
  // que quatre façons de lire la grille en lignes (cf. #lines).
  #move(direction) {
    // `busy` : tant que le coup précédent glisse, un second coup superposerait deux
    // transitions et laisserait des tuiles fantômes.
    if (this.over || this.busy) return

    let moved = false
    let gained = 0
    const absorbed = []

    this.#lines(direction).forEach((indexes) => {
      const line = indexes.map((index) => this.tiles.find((tile) => tile.index === index)).filter(Boolean)
      // Relevé AVANT de déplacer quoi que ce soit : c'est la comparaison avec ces positions
      // qui dit si le coup change réellement la grille. Comparer après coup à la position
      // d'arrivée théorique rendait tout coup « immobile », et la grille ne bougeait jamais.
      const before = line.map((tile) => tile.index)
      let target = 0

      for (let i = 0; i < line.length; i += 1) {
        const tile = line[i]
        const next = line[i + 1]

        if (next && next.value === tile.value) {
          // Les deux tuiles filent sur la même case ; celle du dessous disparaîtra une fois
          // arrivée, l'autre doublera.
          tile.index = indexes[target]
          next.index = indexes[target]
          tile.merging = true
          absorbed.push(next)
          gained += tile.value * 2
          i += 1
        } else {
          tile.index = indexes[target]
        }

        target += 1
      }

      if (line.some((tile, position) => tile.index !== before[position])) moved = true
    })

    if (!moved) {
      this.tiles.forEach((tile) => { tile.merging = false })
      return
    }

    this.busy = true
    this.#position()
    if (gained) this.stage.setScore(this.stage.score + gained)

    this.timer = window.setTimeout(() => this.#settle(absorbed), SLIDE_MS)
  }

  // Après le glissement : les absorbées quittent le plateau, les fusionnées doublent avec un
  // à-coup, et une nouvelle tuile apparaît.
  #settle(absorbed) {
    if (!this.board) return

    absorbed.forEach((tile) => {
      tile.node.remove()
      this.tiles = this.tiles.filter((candidate) => candidate !== tile)
    })

    this.tiles.forEach((tile) => {
      if (!tile.merging) return

      tile.merging = false
      tile.value *= 2
      this.#paint(tile)
      tile.node.classList.add("arcade-2048__tile--merged")
      window.setTimeout(() => tile.node.classList.remove("arcade-2048__tile--merged"), 180)
    })

    this.#spawn()
    this.busy = false

    if (this.tiles.some((tile) => tile.value === WIN)) return this.#finish(this.labelsValue.won)
    if (!this.#movesLeft()) this.#finish(this.labelsValue.stuck)
  }

  // Les index de chaque ligne, ordonnés DANS le sens de la poussée : la première case lue est
  // celle où les tuiles vont s'entasser.
  #lines(direction) {
    const lines = []
    for (let i = 0; i < SIZE; i += 1) {
      const line = []
      for (let j = 0; j < SIZE; j += 1) {
        if (direction === "left") line.push(i * SIZE + j)
        if (direction === "right") line.push(i * SIZE + (SIZE - 1 - j))
        if (direction === "up") line.push(j * SIZE + i)
        if (direction === "down") line.push((SIZE - 1 - j) * SIZE + i)
      }
      lines.push(line)
    }
    return lines
  }

  #spawn() {
    const taken = new Set(this.tiles.map((tile) => tile.index))
    const free = []
    for (let index = 0; index < SIZE * SIZE; index += 1) {
      if (!taken.has(index)) free.push(index)
    }
    if (!free.length) return

    const tile = {
      id: this.nextId,
      value: Math.random() < 0.9 ? 2 : 4,
      index: free[Math.floor(Math.random() * free.length)],
      node: document.createElement("div"),
    }
    this.nextId += 1

    tile.node.classList.add("arcade-2048__tile", "arcade-2048__tile--new")
    this.#paint(tile)
    this.#place(tile)
    this.tileLayer.appendChild(tile.node)
    this.tiles.push(tile)
    window.setTimeout(() => tile.node.classList.remove("arcade-2048__tile--new"), 180)
  }

  #paint(tile) {
    // Une classe par palier plutôt qu'un style en ligne : les couleurs restent dans la
    // feuille de style, avec le reste du mode fun.
    tile.node.className = tile.node.className.replace(/arcade-2048__tile--\d+/, "")
    tile.node.classList.add("arcade-2048__tile", `arcade-2048__tile--${tile.value}`)
    tile.node.textContent = String(tile.value)
  }

  #place(tile) {
    tile.node.style.setProperty("--col", String(tile.index % SIZE))
    tile.node.style.setProperty("--row", String(Math.floor(tile.index / SIZE)))
  }

  #position() {
    this.tiles.forEach((tile) => this.#place(tile))
  }

  #movesLeft() {
    if (this.tiles.length < SIZE * SIZE) return true

    const values = Array.from({ length: SIZE * SIZE }, () => 0)
    this.tiles.forEach((tile) => { values[tile.index] = tile.value })

    for (let row = 0; row < SIZE; row += 1) {
      for (let column = 0; column < SIZE; column += 1) {
        const value = values[row * SIZE + column]
        if (column < SIZE - 1 && value === values[row * SIZE + column + 1]) return true
        if (row < SIZE - 1 && value === values[(row + 1) * SIZE + column]) return true
      }
    }
    return false
  }

  #finish(message) {
    if (this.over) return

    // Un coup peut être en train de glisser quand on arrête : le `settle` différé ne doit
    // pas rouvrir un écran de fin par-dessus celui-ci.
    window.clearTimeout(this.timer)
    this.over = true
    this.stage.finish({ score: this.stage.score, message: message || "" })
  }
}
